import { msg, Trans } from '@lingui/macro'

import * as React from 'react'

import { useTrans } from '~logic/i18n/useTrans'
import {
  FORM_HORIZONTAL_SPACE,
  FORM_INPUT_RADIUS,
  useFormContext,
  useFormState,
} from '~shared/segments/Form'
import { TActiveColorKey } from '~theme/typings'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { Button } from '~ui/components/buttons/Button/Button'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'
import { Input } from '~ui/components/formElements/Input/Input'
import { Grid, GridRow } from '~ui/components/layout/Box'
import { Text } from '~ui/components/typography/Text/Text'

import { TOrderForm } from '../../../contexts/FormProvider'
import { usePromoCodesCtx } from '../contexts/PromoCodeProvider'

type TProps = NoChildren

const GENERIC_ERROR_MESSAGE = msg`Something went wrong while adding the promo code`
const PLACEHOLDER_MESSAGE = msg`GIVEMEFOOD`
const addButtonBgColor: TActiveColorKey = `success`

/**
 * A HTML input for applying promo codes by adding the to the current order
 * and a button to confirm that action.
 */
export const PromoInput: React.FC<TProps> = () => {
  const { trans } = useTrans()
  const { control } = useFormContext<TOrderForm>()
  const { isSubmitting } = useFormState({ control })

  const { addPromoCode, isLoading } = usePromoCodesCtx()
  const [inputValue, setInputValue] = React.useState(``)
  const [isAddingCode, setAddingCode] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState<Nullable<string>>(null)

  const placeholder = trans(PLACEHOLDER_MESSAGE)

  // attempt to submit the user input as a promo code & handle the result
  const submitCode = async () => {
    setAddingCode(true)

    const { wasSuccessful, message } = await addPromoCode(inputValue)

    // code successfully added – clear the input and the potential error message
    // so that user can enter a new one
    if (wasSuccessful) {
      setInputValue(``)
      setErrorMessage(null)
      setAddingCode(false)

      return
    }

    // attempt was unsuccessful and some error message was returned
    // present it to the user so he knows what happened
    if (message) {
      const code = inputValue.trim()
      const enteredPlaceholder = code === placeholder
      const errorMessage = enteredPlaceholder ? trans(msg`Nice try. ${message}`) : message

      setErrorMessage(errorMessage)
      setAddingCode(false)

      return
    }

    // attempt was unsuccessful and we have no error
    // present some generic error
    setErrorMessage(trans(GENERIC_ERROR_MESSAGE))
    setAddingCode(false)
  }

  // attempt to submit the code at Enter press as well
  const submitOnEnter: PropsOf<typeof Input>['onKeyDown'] = (event) => {
    if (event.key === 'Enter') {
      submitCode()
      event.preventDefault()
    }
  }

  // if the code is being validated or codes are being loaded,
  // show a message about that,
  // otherwise show the input value
  const value = React.useMemo(() => {
    if (isAddingCode) return trans(msg`Validating ${inputValue}...`)
    if (isLoading) return trans(msg`Loading...`)

    return inputValue
  }, [inputValue, isAddingCode, isLoading, trans])

  return (
    <Grid>
      <InputControl>
        <InputLabel>
          <Trans>Promo code</Trans>
        </InputLabel>
        <GridRow gap={FORM_HORIZONTAL_SPACE} gridTemplateColumns={`1fr auto`}>
          <Input
            autoComplete={`off`}
            onChange={setInputValue}
            onKeyDown={submitOnEnter}
            placeholder={placeholder}
            isDisabled={isAddingCode || isSubmitting || isLoading}
            // change the value when the input is disabled while validating the code
            // to present some more meaningful message about what is going on
            value={value}
            radius={{
              topRight: `none`,
              bottomRight: `none`,
              topLeft: FORM_INPUT_RADIUS,
              bottomLeft: FORM_INPUT_RADIUS,
            }}
          />
          <Button
            onClick={submitCode}
            bgColor={addButtonBgColor}
            state={isAddingCode || isSubmitting || isLoading ? `disabled` : `enabled`}
            padding={{ vertical: `small`, horizontal: `large` }}
            radius={{
              topLeft: `none`,
              bottomLeft: `none`,
              topRight: FORM_INPUT_RADIUS,
              bottomRight: FORM_INPUT_RADIUS,
            }}
          >
            <Text fontStyle={`upperCase`}>
              <Trans>apply</Trans>
            </Text>
          </Button>
        </GridRow>
        <AnimatedHeight isOpen={Boolean(errorMessage)}>
          <InputErrorMessage>{errorMessage}</InputErrorMessage>
        </AnimatedHeight>
      </InputControl>
    </Grid>
  )
}
